import { useState, useEffect } from 'react';

/**
 * Hook personnalisé pour vérifier si une nouvelle version de l'application est disponible
 * @param {number} checkInterval - Intervalle de vérification en millisecondes (par défaut: 5 minutes)
 * @returns {object} - { isNewVersionAvailable, currentVersion, latestVersion, checkVersion }
 */
export function useVersionCheck(checkInterval = 5 * 60 * 1000) {
    const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [latestVersion, setLatestVersion] = useState(null);

    const checkVersion = async () => {
        try {
            // Récupérer la version actuelle stockée lors du premier chargement
            let localVersion = localStorage.getItem('app-version');

            // Ajouter un timestamp pour éviter le cache du navigateur
            const timestamp = new Date().getTime();
            const response = await fetch(`/version.json?t=${timestamp}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (response.ok) {
                const versionInfo = await response.json();
                const serverVersion = versionInfo.version;

                // Si c'est la première vérification, stocker la version actuelle
                if (!localVersion) {
                    localStorage.setItem('app-version', serverVersion);
                    setCurrentVersion(serverVersion);
                    setLatestVersion(serverVersion);
                    localVersion = serverVersion;
                }

                // Comparer les versions
                if (localVersion !== serverVersion) {
                    console.log(`Nouvelle version détectée: ${serverVersion} (actuelle: ${localVersion})`);
                    setIsNewVersionAvailable(true);
                    setCurrentVersion(localVersion);
                    setLatestVersion(serverVersion);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la vérification de la version:', error);
        }
    };

    useEffect(() => {
        // Vérification initiale
        checkVersion();

        // Vérifications périodiques
        const intervalId = setInterval(checkVersion, checkInterval);

        // Nettoyage
        return () => clearInterval(intervalId);
    }, [checkInterval]);

    return {
        isNewVersionAvailable,
        currentVersion,
        latestVersion,
        checkVersion
    };
}
